import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";

export interface Command {
  id: string;
  label: string;
  group: string;
  icon?: JSX.Element;
  hint?: string;
  keywords?: string;
  run: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  commands: Command[];
  onClose: () => void;
}

const matchesQuery = (command: Command, query: string) => {
  if (!query) return true;
  const haystack = `${command.label} ${command.group} ${command.keywords ?? ""}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token));
};

export const CommandPalette = ({ open, commands, onClose }: CommandPaletteProps) => {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => commands.filter((command) => matchesQuery(command, query)), [commands, query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  const run = (index: number) => {
    const command = filtered[index];
    if (!command) return;
    onClose();
    command.run();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => (filtered.length ? (index + 1) % filtered.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => (filtered.length ? (index - 1 + filtered.length) % filtered.length : 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      run(active);
    } else if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    } else if (event.key === "Home") {
      event.preventDefault();
      setActive(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActive(Math.max(0, filtered.length - 1));
    }
  };

  // Group the (already filtered) commands while keeping a flat index for keyboard nav.
  let flatIndex = -1;
  const groups: { name: string; items: { command: Command; index: number }[] }[] = [];
  for (const command of filtered) {
    flatIndex += 1;
    const index = flatIndex;
    let group = groups.find((entry) => entry.name === command.group);
    if (!group) {
      group = { name: command.group, items: [] };
      groups.push(group);
    }
    group.items.push({ command, index });
  }

  return (
    <div className="palette-backdrop" role="presentation" onClick={onClose}>
      <div
        className="command-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="palette-search">
          <Search size={18} aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            placeholder="Type a command or search…"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Search commands"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="palette-esc">Esc</kbd>
        </div>

        <div className="palette-list" ref={listRef} role="listbox" aria-label="Commands">
          {filtered.length === 0 ? (
            <div className="palette-empty">No matching commands</div>
          ) : (
            groups.map((group) => (
              <div className="palette-group" key={group.name}>
                <div className="palette-group-label">{group.name}</div>
                {group.items.map(({ command, index }) => (
                  <button
                    key={command.id}
                    type="button"
                    data-index={index}
                    role="option"
                    aria-selected={index === active}
                    className={index === active ? "palette-item active" : "palette-item"}
                    onMouseMove={() => index !== active && setActive(index)}
                    onClick={() => run(index)}
                  >
                    {command.icon && <span className="palette-item-icon">{command.icon}</span>}
                    <span className="palette-item-label">{command.label}</span>
                    {command.hint && <kbd className="palette-item-hint">{command.hint}</kbd>}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="palette-footer">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> select
          </span>
          <span>
            <kbd>esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
};
